import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SelectedMenuOptions } from "../components/layout/Layout";
import { getMenuPathname } from "../helpers/MenuPathHelper";

interface MenuContextType {
  selectedMenu: SelectedMenuOptions;
  setSelectedMenu: (menu: SelectedMenuOptions) => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error("useMenu must be used within a MenuProvider");
  }
  return context;
};

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  const getMenuFromPath = (pathname: string): SelectedMenuOptions => {
    if (pathname === "/") {
      return SelectedMenuOptions.Dashboard;
    }

    const paths = getMenuPathname(pathname)
      .split("/")
      .filter((p) => p !== "");
    const firstSegment = paths[0];

    if (firstSegment === "admin") {
      return SelectedMenuOptions.Admin;
    }
    if (firstSegment === "pricings") {
      return SelectedMenuOptions.Pricings;
    }
    if (firstSegment === "promotions") {
      return SelectedMenuOptions.Promotions;
    }
    if (firstSegment === "fidelity") {
      return SelectedMenuOptions.Fidelity;
    }
    if (firstSegment === "commercial-intelligence") {
      return SelectedMenuOptions.Promotions;
    }
    if (firstSegment === "commercial-analysis") {
      return SelectedMenuOptions.Promotions;
    }
    if (firstSegment === "purchaseorders") {
      return SelectedMenuOptions.PurchaseOrders;
    }
    if (firstSegment === "repositionorders") {
      return SelectedMenuOptions.RepositionOrders;
    }
    if (firstSegment === "dashboard" || firstSegment === "") {
      return SelectedMenuOptions.Dashboard;
    }

    if (
      Object.values(SelectedMenuOptions).includes(
        firstSegment as SelectedMenuOptions
      )
    ) {
      return firstSegment as SelectedMenuOptions;
    }

    return SelectedMenuOptions.Dashboard;
  };

  const [selectedMenu, setSelectedMenu] = useState<SelectedMenuOptions>(() => {
    return getMenuFromPath(window.location.pathname);
  });

  useEffect(() => {
    const menu = getMenuFromPath(location.pathname);
    setSelectedMenu(menu);
  }, [location]);

  return (
    <MenuContext.Provider value={{ selectedMenu, setSelectedMenu }}>
      {children}
    </MenuContext.Provider>
  );
};
